/**
 * Browser- and edge-safe error contract.
 *
 * This module deliberately has no telemetry or runtime-specific imports. The
 * Forge SPA uses these client-facing values, while `errors.ts` adds the
 * Deno-only observability that edge handlers need. Keeping that boundary here
 * prevents browser bundles from reaching edge monitoring (and node:async_hooks).
 */

export interface SafeError {
  code: SafeErrorCode;
  message: string;
  requestId: string;
  details?: never;
}

export const SAFE_ERROR_CODES = {
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  FORBIDDEN_ORIGIN: "forbidden_origin",
  NOT_FOUND: "not_found",
  INVALID_REQUEST: "invalid_request",
  INVALID_CREDENTIALS: "invalid_credentials",
  RATE_LIMITED: "rate_limited",
  VALIDATION_FAILED: "validation_failed",
  TOKEN_EXPIRED: "token_expired",
  TOKEN_REQUIRED: "token_required",
  INTERNAL_ERROR: "internal_error",
  SERVICE_UNAVAILABLE: "service_unavailable",
  SECURITY_BLOCK: "security_block",
  SESSION_EXPIRED: "session_expired",
  INTEGRITY_ERROR: "integrity_error",
} as const;

export type SafeErrorCode = (typeof SAFE_ERROR_CODES)[keyof typeof SAFE_ERROR_CODES];

export const SAFE_ERROR_MESSAGES: Record<SafeErrorCode, string> = {
  [SAFE_ERROR_CODES.UNAUTHORIZED]: "Unauthorized",
  [SAFE_ERROR_CODES.FORBIDDEN]: "Forbidden",
  [SAFE_ERROR_CODES.FORBIDDEN_ORIGIN]: "Request origin is not allowed",
  [SAFE_ERROR_CODES.NOT_FOUND]: "Resource not found",
  [SAFE_ERROR_CODES.INVALID_REQUEST]: "Invalid request",
  [SAFE_ERROR_CODES.INVALID_CREDENTIALS]: "Invalid credentials",
  [SAFE_ERROR_CODES.RATE_LIMITED]: "Too many requests",
  [SAFE_ERROR_CODES.VALIDATION_FAILED]: "Validation failed",
  [SAFE_ERROR_CODES.TOKEN_EXPIRED]: "Session expired",
  [SAFE_ERROR_CODES.TOKEN_REQUIRED]: "Session token is required",
  [SAFE_ERROR_CODES.SESSION_EXPIRED]: "Session expired",
  [SAFE_ERROR_CODES.INTERNAL_ERROR]: "An unexpected error occurred",
  [SAFE_ERROR_CODES.SERVICE_UNAVAILABLE]: "Service temporarily unavailable",
  [SAFE_ERROR_CODES.SECURITY_BLOCK]: "Request blocked for security reasons",
  [SAFE_ERROR_CODES.INTEGRITY_ERROR]: "Request rejected due to integrity checks",
};

const DEFAULT_REQUEST_ID = "unknown";
const SAFE_CODE_SET = new Set<SafeErrorCode>(Object.values(SAFE_ERROR_CODES));

export const isSafeErrorCodeCandidate = (code: unknown): code is SafeErrorCode =>
  typeof code === "string" && SAFE_CODE_SET.has(code as SafeErrorCode);

export const statusToSafeCode = (status: number): SafeErrorCode | null => {
  if (status === 401) return SAFE_ERROR_CODES.UNAUTHORIZED;
  if (status === 403) return SAFE_ERROR_CODES.FORBIDDEN;
  if (status === 404) return SAFE_ERROR_CODES.NOT_FOUND;
  if (status === 409 || status === 422) return SAFE_ERROR_CODES.VALIDATION_FAILED;
  if (status === 429) return SAFE_ERROR_CODES.RATE_LIMITED;
  if (status >= 500) return SAFE_ERROR_CODES.SERVICE_UNAVAILABLE;
  if (status >= 400) return SAFE_ERROR_CODES.INVALID_REQUEST;
  return null;
};

const toSafeCode = (error: unknown, status = 500): SafeErrorCode => {
  if (error && typeof error === "object") {
    const candidate = error as { code?: unknown; status?: unknown };
    if (isSafeErrorCodeCandidate(candidate.code)) return candidate.code;

    if (typeof candidate.status === "number") {
      const fromStatus = statusToSafeCode(candidate.status);
      if (fromStatus) return fromStatus;
    }
  }

  return statusToSafeCode(status) || SAFE_ERROR_CODES.INTERNAL_ERROR;
};

export const safeRequestIdFromHeaders = (headers?: Headers | null): string =>
  headers
    ? headers.get("x-request-id") ||
      headers.get("x-correlation-id") ||
      headers.get("x-trace-id") ||
      crypto.randomUUID()
    : DEFAULT_REQUEST_ID;

const safeErrorFor = (error: unknown, status: number, requestId: string): SafeError => {
  const code = toSafeCode(error, status);
  return {
    code,
    message: SAFE_ERROR_MESSAGES[code] ?? SAFE_ERROR_MESSAGES[SAFE_ERROR_CODES.INTERNAL_ERROR],
    requestId,
  };
};

/**
 * Convert untrusted edge-function failures to the stable public error shape.
 * The edge-only module records telemetry separately; this browser-safe mapper
 * must remain free of Deno and Node dependencies.
 */
export const toClientError = (
  error: unknown,
  requestHeaders?: Headers,
  fallbackCode: SafeErrorCode = SAFE_ERROR_CODES.INTERNAL_ERROR,
): SafeError => {
  const requestId = requestHeaders ? safeRequestIdFromHeaders(requestHeaders) : DEFAULT_REQUEST_ID;
  if (!error) {
    return {
      code: fallbackCode,
      message: SAFE_ERROR_MESSAGES[fallbackCode],
      requestId,
    };
  }

  if (typeof error === "number") {
    return safeErrorFor(error, error, requestId);
  }

  if (error && typeof error === "object" && "code" in (error as Record<string, unknown>)) {
    const code = (error as { code?: unknown }).code;
    if (isSafeErrorCodeCandidate(code)) {
      return { code, message: SAFE_ERROR_MESSAGES[code], requestId };
    }
  }

  const status =
    error && typeof error === "object" && "status" in (error as Record<string, unknown>)
      ? Number((error as Record<string, unknown>).status)
      : undefined;

  if (status && Number.isFinite(status) && statusToSafeCode(status)) {
    return safeErrorFor(status, status, requestId);
  }

  return safeErrorFor(error, 500, requestId);
};
